import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ModalComponent from 'components/common/ModalComponent'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'

const AgroDataEditModal = ({ isOpen, handleSave, handleClose, selectedAgroData, isBulk, bulkCount, farmId }) => {
  const { t } = useTranslation()
  const [activities, setActivities] = useState([])
  const [workers, setWorkers] = useState([])

  useEffect(() => {
    if (!isOpen || !farmId) return
    const token = localStorage.getItem('secret')
    const headers = { Authorization: `Bearer ${token}` }

    makeRequest({ method: 'GET', url: `/management/activities/${farmId}`, headers })
      .then(r => setActivities(r.data ?? []))
      .catch(err => console.error('Failed to load activities', err))

    makeRequest({ method: 'GET', url: `/management/workers-management/${farmId}/all`, headers })
      .then(r => setWorkers(r.data ?? []))
      .catch(err => console.error('Failed to load workers', err))
  }, [isOpen, farmId])

  const getInitialData = () => {
    if (isBulk) return { activityId: '', workerId: '', completionPercentage: '' }
    return {
      activityId: selectedAgroData?.activityId ?? '',
      workerId: selectedAgroData?.workerId ?? '',
      completionPercentage: selectedAgroData?.completionPercentage ?? ''
    }
  }

  const handleSaveClick = (formData) => {
    if (!formData) {
      showAlert({ text: t('mgmt.dataEditModal.formMissing'), icon: 'error' })
      return
    }

    const payload = {}
    if (formData.activityId !== '' && formData.activityId != null) payload.activityId = Number(formData.activityId)
    if (formData.workerId !== '' && formData.workerId != null) payload.workerId = Number(formData.workerId)
    if (formData.completionPercentage !== '' && formData.completionPercentage != null) {
      const pct = parseFloat(formData.completionPercentage)
      if (pct < 0 || pct > 100) {
        showAlert({ text: t('mgmt.dataEditModal.completionRange'), icon: 'warning' })
        return
      }
      payload.completionPercentage = pct
    }

    if (Object.keys(payload).length === 0) {
      showAlert({ text: t('mgmt.dataEditModal.noChanges'), icon: 'info' })
      return
    }

    handleSave(payload)
  }

  const title = isBulk
    ? t('mgmt.dataEditModal.editCount', { count: bulkCount })
    : t('mgmt.dataEditModal.agroTitle')

  return (
    <ModalComponent
      title={title}
      isOpen={isOpen}
      onClose={handleClose}
      onSave={handleSaveClick}
      initialData={getInitialData()}
      key={isOpen ? 'open' : 'closed'}
    >
      {({ formData, handleChange }) => (
        <form className='space-y-4 w-72' autoComplete='off'>
          {isBulk && (
            <p className='text-sm text-gray-500'>{t('mgmt.dataEditModal.onlyFilled')}</p>
          )}

          <label className='block text-sm font-medium'>
            {t('mgmt.dataEditModal.activity')}
            <select
              name='activityId'
              value={formData?.activityId ?? ''}
              onChange={handleChange}
              className='w-full mt-1 p-2 border rounded'
            >
              <option value=''>{t('mgmt.dataEditModal.keepCurrent')}</option>
              {activities.map(a => (
                <option key={a.activityId} value={a.activityId}>{a.activityName}</option>
              ))}
            </select>
          </label>

          <label className='block text-sm font-medium'>
            {t('mgmt.dataEditModal.worker')}
            <select
              name='workerId'
              value={formData?.workerId ?? ''}
              onChange={handleChange}
              className='w-full mt-1 p-2 border rounded'
            >
              <option value=''>{t('mgmt.dataEditModal.keepCurrent')}</option>
              {workers.map(w => (
                <option key={w.workerId} value={w.workerId}>
                  {w.firstName} {w.lastName}
                </option>
              ))}
            </select>
          </label>

          <label className='block text-sm font-medium'>
            {t('mgmt.dataEditModal.completion')}
            <input
              type='number'
              name='completionPercentage'
              min={0}
              max={100}
              step={0.01}
              value={formData?.completionPercentage ?? ''}
              onChange={handleChange}
              placeholder={t('mgmt.dataEditModal.leaveBlank')}
              className='w-full mt-1 p-2 border rounded'
            />
          </label>
        </form>
      )}
    </ModalComponent>
  )
}

export default AgroDataEditModal
